import React, { type ReactElement } from 'react';
import clsx from 'clsx';
import Header from '@pixum/header';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { CardCollection } from '../cardCollection';
import { MagazineCollection } from '../magazineCollection';
import { UspCollection } from '../uspCollection';
import { InfoActionBanner } from '../infoActionBanner';
import { InfoBanner } from '../infoBanner';
import { AwardCollection } from '../awardCollection';
import { AuthorBox } from '../authorBox';
import { OrderWayCollection } from '../orderWayCollection';
import { CustomHtml } from '../customHtml';
import { AnchorLinkCollection } from '../anchorLinkCollection';
import { FaqCollection } from '../faqCollection';
import { ComparisonTable } from '../comparisonTable';
import { DownloadStepsWithSkeleton } from '../downloadSoftware/downloadSteps/DownloadStepsWithSkeleton';
import type { DeviceOrientedComponentProps } from '../../types';
import { EditorialSectionCollection } from '../editorialSection';
import { NewsletterMagazine } from '../newsletter';
import { RichTextBody, isRichTextEmpty } from '../richtText';
import { THEME_DEFAULT_PROPS, CONTENTFUL_ARTICLE_TYPES } from 'data-layer/constants';
import type {
  ArticleNode,
  ContentfulCardModuleType,
  CustomHtmlNode,
  GroupedMagazineNode,
  MagazineNode,
  OrderStatusNode,
  MentionMeNode
} from 'data-layer';
import { MENTION_ME_CONTAINER_ID } from 'hooks';
import { MentionMeController, MentionMeSkeleton } from '../mentionme';
import type { LocaleIso } from 'utils';
import { OrderStatus } from '../orderStatus';

export interface ArticlesContentProps extends DeviceOrientedComponentProps {
  articles: (ArticleNode | CustomHtmlNode | MentionMeNode)[];
  defaultMobileLayout: boolean;
  locale: LocaleIso;
  isFooterContent?: boolean;
}

/**
 * Type guard to check if a node is a CustomHtmlNode
 */
function isCustomHtmlNode(node: ArticleNode | CustomHtmlNode | OrderStatusNode): node is CustomHtmlNode {
  return 'customHtml' in node && typeof node.customHtml === 'string';
}

function isOrderStatusNode(node: ArticleNode | CustomHtmlNode | OrderStatusNode): node is OrderStatusNode {
  return node.transformerType === 'OrderStatusTransformer';
}

/**
 * Type guard to check if a node is a MentionMe
 */
function isMentionMeNode(node: ArticleNode | MentionMeNode): node is MentionMeNode {
  return node.transformerType === 'MentionMeTransformer';
}

/**
 * Type guard to check if a node is a download steps article (identified by platformKey)
 */
function isDownloadStepsArticle(node: ArticleNode | CustomHtmlNode | MentionMeNode): node is ArticleNode {
  return !!(node as ArticleNode).platformKey;
}

/**
 * Type guard to check if a node is a newsletter article
 */
function isNewsletterArticle(node: ArticleNode): boolean {
  return node.type === CONTENTFUL_ARTICLE_TYPES.Newsletter;
}

/**
 * ArticlesContent Component:
 * This component is responsible for rendering a collection of articles, each represented as a section.
 * It dynamically handles various types of content within each article, including:
 *
 * - Title: Displays the article's title using the Header component with specific styling and accessibility attributes.
 * - Magazines: Renders a collection of magazines using the MagazineCollection component, with support for mobile layout.
 * - Cards: Displays a collection of cards using the CardCollection component, applying themes, types, and layouts as specified.
 * - InfoBanner: Shows an informational banner using the InfoBanner component, if provided in the article data.
 * - USP (Unique Selling Points): Lists unique selling points using the UspCollection component.
 * - Awards: Displays a collection of awards using the AwardCollection component.
 * - Order Ways: Displays a list of order methods using the OrderWayCollection component.
 * - Editorial Sections: Renders editorial image/text sections using the EditorialSectionCollection component.
 *
 * Accessibility and layout considerations are applied based on the article's properties, ensuring a user-friendly and visually appealing experience.
 */
export function ArticlesContent(props: ArticlesContentProps): ReactElement {
  const { articles, defaultMobileLayout, device, isFooterContent = false, locale } = props;

  // Collect download-steps articles (identified by platformKey) for DownloadStepsWithSkeleton
  const downloadStepsArticles = articles.filter(isDownloadStepsArticle);

  return (
    <>
      {articles.map((node, index) => {
        const article = node as ArticleNode;

        // Handle regular articles (existing logic)
        const hasEditorial = !!article.editorialSections && article.editorialSections.length > 0;
        const hasAnchorLinks = article?.anchorLinks && article.anchorLinks.length > 0;
        const hasNavigationCards = article.navigationCards && article.navigationCards.length > 0;
        const hasProductCards = article.productCards && article.productCards.length > 0;
        const hasCards = hasNavigationCards || hasProductCards;
        const hasMagazines = article.magazines && article.magazines.length > 0;
        const newsletter = isNewsletterArticle(article) ? article.newsletter : undefined;

        /**
         * GroupedCard nodes are now embedded in productCards[] at correct Contentful positions
         * (handled by ArticleTransformer.transformProductCardArticle).
         * For navigation cards, grouped cards are simply appended.
         */
        const allCards = hasNavigationCards
          ? (article.navigationCards ?? [])
          : hasProductCards
            ? (article.productCards ?? [])
            : [];

        // Grouped magazines are embedded in magazines[] at correct Contentful positions.
        const allMagazines = (article.magazines ?? []) as (MagazineNode | MagazineNode[] | GroupedMagazineNode)[];
        const hasActionBanner = article.infoBanner;
        const hasInfoActionBanner = article.infoActionBanner;
        const hasUsp = article.usp;
        const hasTable = article.table;
        const hasAwards = article?.awards && article.awards.length > 0;
        const hasAuthorBox = !!article?.authorBox;
        const hasOrderWay = article?.orderWays && article.orderWays.length > 0;
        const hasOrderWayDialog = article?.orderWaysDialog;
        const hasFaq = article?.faq?.items && article.faq.items.length > 0;
        const shouldBeCentered =
          hasAnchorLinks || hasUsp || hasCards || hasAwards || hasOrderWay || hasFaq || hasTable || hasEditorial;
        const shouldHaveCenteredHeader = hasAwards || hasActionBanner || hasAnchorLinks;
        const showArticleHeader = article.title && !hasFaq && !hasOrderWayDialog && !hasActionBanner;
        const showArticleIntro =
          showArticleHeader &&
          !hasTable &&
          article.type === CONTENTFUL_ARTICLE_TYPES.Editorial &&
          !!article.intro &&
          !isRichTextEmpty(article.intro);
        const headingId = `${article?.internalName}-heading`;
        const sectionProps: React.HTMLAttributes<HTMLElement> = {
          id: article?.internalName,
          className: clsx(
            isFooterContent ? 'footer-section' : 'main-section',
            shouldBeCentered && 'center--content',
            hasOrderWay && 'order-ways-container',
            hasMagazines && 'magazines-container',
            hasEditorial && 'editorial-container'
          ),
          ...(showArticleHeader
            ? { role: article.accessibility?.role, 'aria-labelledby': headingId }
            : article.title
              ? { role: article.accessibility?.role, 'aria-label': article.title }
              : { role: article.accessibility?.role, 'aria-label': `article-label-${article.accessibility?.label}` }),
          // Add data-platform-key for client-side filtering
          ...(article.platformKey && { 'data-platform-key': article.platformKey })
        };

        if (isOrderStatusNode(node)) {
          return <OrderStatus key={node.id || `order-status-${index}`} data={node} device={device} />;
        }

        // Handle CustomHtmlNode — no aria attributes since CustomHtml is not a labelled article section
        if (isCustomHtmlNode(node)) {
          const customHtmlSectionProps: React.HTMLAttributes<HTMLElement> = {
            id: node.internalName,
            className: isFooterContent ? 'footer-section' : 'main-section'
          };

          return (
            <CustomHtml key={node.id || `custom-html-${index}`} node={node} sectionProps={customHtmlSectionProps} />
          );
        }

        if (isMentionMeNode(node)) {
          return (
            <React.Fragment key={node.id || `mention-me-${index}`}>
              <div id={MENTION_ME_CONTAINER_ID}>
                <MentionMeSkeleton />
              </div>
              <MentionMeController locale={locale} config={node} />
            </React.Fragment>
          );
        }

        // Skip download-steps articles after the first one
        if (isDownloadStepsArticle(node) && downloadStepsArticles[0]?.id !== article.id) {
          return null;
        }

        const headerAlignment = shouldHaveCenteredHeader ? 'center' : 'left';
        const hasFootnote = article.dynamicFootnote && article.dynamicFootnote.trim().length > 0;

        return (
          <section key={article.id} {...sectionProps}>
            {showArticleHeader && !hasTable && (
              <Header
                headlineText={article.title}
                headlineVariant={TEXT_VARIANT.TITLE_2}
                id={headingId}
                level={2}
                subHeadline={article.subtitle}
                subHeadlineVariant={TEXT_VARIANT.TITLE_3}
                textAlignment={headerAlignment}
              />
            )}
            {showArticleIntro && <RichTextBody text={article.intro} className="article__intro" />}
            {hasTable && (
              <ComparisonTable
                title={article.title}
                subtitle={article.subtitle}
                attributes={article.table?.attributes}
                columns={article.table?.columns}
                description={article.table?.description}
                subject={article.table?.subject}
                priceLabel={article.table?.priceLabel}
                device={device}
                theme={article.theme || THEME_DEFAULT_PROPS}
              />
            )}
            {hasAnchorLinks && <AnchorLinkCollection anchorLinks={article.anchorLinks} device={device} />}
            {hasMagazines && !isDownloadStepsArticle(node) && (
              <MagazineCollection magazines={allMagazines} defaultMobileLayout={defaultMobileLayout} device={device} />
            )}
            {hasMagazines && isDownloadStepsArticle(node) && (
              <DownloadStepsWithSkeleton articles={downloadStepsArticles} device={device} />
            )}
            {newsletter && <NewsletterMagazine props={newsletter} articleTheme={article.theme} />}
            {hasCards && (
              <CardCollection
                cards={allCards}
                device={device}
                theme={article.theme || THEME_DEFAULT_PROPS}
                type={article.type as ContentfulCardModuleType}
                layout={article.layout || ''}
              />
            )}
            {hasActionBanner && <InfoBanner {...article.infoBanner} device={device} />}
            {hasInfoActionBanner && <InfoActionBanner {...article.infoActionBanner} device={device} />}
            {hasUsp && <UspCollection device={device} uspList={article.usp ?? []} />}
            {hasAwards && <AwardCollection device={device} awards={article?.awards ?? []} />}
            {hasAuthorBox && article.authorBox && <AuthorBox authorBox={article.authorBox} device={device} />}
            {hasOrderWay && (
              <OrderWayCollection
                device={device}
                orderWays={article.orderWays ?? []}
                theme={article.theme || THEME_DEFAULT_PROPS}
                layout={article.layout || ''}
              />
            )}
            {hasFaq && article.faq && (
              <FaqCollection
                device={device}
                faq={article.faq}
                articleTitle={article.title}
                articleInternalName={article.internalName}
              />
            )}
            {hasEditorial && article.editorialSections && (
              <EditorialSectionCollection
                sections={article.editorialSections}
                device={device}
                sectionHeadingLevel={showArticleHeader ? 3 : 2}
              />
            )}
            {hasFootnote && <Text variant={TEXT_VARIANT.FOOTNOTE}>{article.dynamicFootnote}</Text>}
          </section>
        );
      })}
    </>
  );
}
